import { onAuthStateChanged } from 'firebase/auth';

import { queryClient } from '@/app/providers/queryClient';
import { analytics, auth, logger, setUser } from '@/shared';

import { sessionStore } from './store.js';
import type { SessionUser } from './types.js';

let stopListening: (() => void) | null = null;

function toSessionUser(user: import('firebase/auth').User): SessionUser {
  return {
    id: user.uid,
    phoneNumber: user.phoneNumber ?? undefined,
    isAnonymous: user.isAnonymous,
  };
}

export function startSessionListener(): () => void {
  if (stopListening) return stopListening;

  logger.info('session listener starting', { source: 'entities/session' });

  const unsub = onAuthStateChanged(auth, (user) => {
    if (!user) {
      logger.info('session state: unauthenticated', { source: 'entities/session' });
      void analytics.track({
        name: 'session.unauthenticated',
        context: { actor: 'clinician' },
      });
      setUser(null);
      queryClient.clear();
      sessionStore.set({ status: 'unauthenticated', user: null });
      return;
    }

    logger.info('session state: authenticated', {
      source: 'entities/session',
      isAnonymous: user.isAnonymous,
      hasPhoneNumber: user.phoneNumber != null,
    });
    void analytics.track({
      name: 'session.authenticated',
      context: { actor: 'clinician' },
      properties: {
        isAnonymous: user.isAnonymous,
        hasPhoneNumber: user.phoneNumber != null,
      },
    });
    setUser({
      firebaseUserId: user.uid,
      phoneNumber: user.phoneNumber ?? undefined,
    });
    sessionStore.set({ status: 'authenticated', user: toSessionUser(user) });
  });

  stopListening = () => {
    logger.info('session listener stopping', { source: 'entities/session' });
    unsub();
    stopListening = null;
  };

  return stopListening;
}
